import { Box, Chip, Typography } from '@mui/material';
import React from 'react';
import type { TableRow } from '../../template/Table';
import TableMobileViewActions from './TableMobileViewActions';
import { useNativeContentManagementRenderer } from './useNativeContentManagementRenderer';
import type { useTableMobileView } from './useTableMobileView';

type MobileView = ReturnType<typeof useTableMobileView>;

/** Matches MobileAppPreview's phone content width, so wrapping/sizing looks the same. */
const PREVIEW_CONTENT_WIDTH = 343;

function mobileViewStatus(isDraft: boolean, hasSaved: boolean) {
  if (isDraft) return { label: 'AI draft — not yet saved', color: 'warning' as const };
  if (hasSaved) return { label: 'Saved AI override', color: 'success' as const };
  return { label: 'Automatic layout', color: 'default' as const };
}

/**
 * The table's current mobile rendering — shown in the Table block editor's
 * Mobile tab, loaded through the ACTUAL native app Table component (the
 * same `NativeAppPreview/ContentManagement` alias MobileAppPreview uses for
 * the page-level device-frame preview), so it's pixel-accurate to the app
 * rather than a hand-rolled mock-up. With no AI override, that component
 * falls back to its own automatic (non-AI) layout on its own — no separate
 * logic is needed here to reproduce that. "Refine with AI" lives in this
 * same tab, so the admin always sees the current rendering before deciding
 * whether an AI override is worth generating.
 */
export default function TableMobileViewInlinePreview({
  mobileView,
  rows,
}: {
  mobileView: MobileView;
  rows: TableRow[];
}) {
  const { override, isDraft, hasSaved, busy, confirmDraft, discardDraft, remove, generate } =
    mobileView;
  const Renderer = useNativeContentManagementRenderer();

  const previewContent = [
    {
      type: 'Table',
      id: 'table-mobile-view-preview',
      properties: override ? { rows, mobileView: override } : { rows },
    },
  ];

  const { label: statusLabel, color: statusColor } = mobileViewStatus(isDraft, hasSaved);

  return (
    <Box
      data-testid="table-mobile-view-preview"
      sx={{ border: '1px dashed #c7c7c7', borderRadius: 1, p: 1.5, bgcolor: '#fafafa' }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="caption" sx={{ fontWeight: 600 }}>
          Mobile preview
        </Typography>
        <Chip size="small" label={statusLabel} color={statusColor} variant="outlined" />
      </Box>

      <Box sx={{ maxWidth: PREVIEW_CONTENT_WIDTH, bgcolor: '#fff', borderRadius: 1, p: 1 }}>
        {Renderer ? (
          <Renderer content={previewContent} contentWidth={PREVIEW_CONTENT_WIDTH} />
        ) : (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Loading preview…
          </Typography>
        )}
      </Box>

      <TableMobileViewActions
        isDraft={isDraft}
        hasSaved={hasSaved}
        busy={busy}
        confirmDraft={confirmDraft}
        discardDraft={discardDraft}
        remove={remove}
        generate={generate}
      />
    </Box>
  );
}
